// Онбординг, экран 3 — «Сколько времени в день» (план §5.3).
//
// Единственный вопрос онбординга кроме языка — и он остаётся ровно потому, что
// немедленно и видимо меняет продукт: заполняется учебный календарь (инвариант
// §10.8 «вопрос без последствий не задаём»). Лид на экране это и обещает.
//
// ─────────────────────────────────────────────────────────────────────────────
// Wire-up, который нельзя потерять
// ─────────────────────────────────────────────────────────────────────────────
// Пишем ДВУМЯ СУЩЕСТВУЮЩИМИ процедурами с клиента:
//
//   `studyPlan.loadSettings.update` — `dailyMinutesCap` + `scheduleDays`;
//   `streaks.updateSettings`        — `dailyGoalMinutes` (то самое «0 / 30 мин»
//                                     в топбаре, человек сразу увидит связь).
//
// Своя серверная мутация поверх хелпера `updateLoadSettings()` запрещена: эмит
// `study-plan/reschedule.requested` живёт в РОУТЕРЕ процедуры
// (`study-plan.router.ts`), а не в хелпере. Потеряв эмит, мы получили бы
// несобранный календарь при собранных настройках — то есть текст лида стал бы
// враньём.
//
// Порядок вызовов: сначала нагрузка (она и запускает пересборку календаря),
// потом дневная норма. Провал первой оставляет человека на экране — обещание
// не выполнено, уводить нельзя. Провал второй не отменяет первую: календарь уже
// пересобирается, и врать «не сохранилось» тоже нельзя — говорим о частичной
// неудаче и идём дальше.
//
// Пропуск НИЧЕГО не пишет. Это не мелочь: `hasRhythm` в чек-листе первых шагов
// проверяет наличие строки `user_study_load_settings`, и запись «дефолта» при
// пропуске закрыла бы пункт «Настроить ритм занятий» за пользователя, который
// его как раз пропустил. Дефолт 30/будни — это предзаполнение формы, а не
// молчаливая запись.
//
// ─────────────────────────────────────────────────────────────────────────────
// Черновик ≠ настройки
// ─────────────────────────────────────────────────────────────────────────────
// Введённое сохраняется НА КАЖДОЕ ИЗМЕНЕНИЕ — но в `onboarding_state.draft`
// (`onboarding.saveDraft`), а не в реальные настройки. Разница принципиальная и
// ровно та же, что с пропуском: автозапись в `user_study_load_settings` закрыла
// бы пункт чек-листа за неподтвердившего человека и дёргала бы пересборку
// календаря на каждый ввод цифры. Настройки по-прежнему пишет только «Далее».
//
// Из-за этого экран ждёт `getProgress` перед показом формы: значения приходят с
// сервера, и посеять их в уже редактируемую форму означало бы либо затереть
// ввод, либо показать дефолт вместо сохранённого. Ожидание — один запрос.

import type { OnboardingDraft } from "@studyhub/shared/contracts/onboarding";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { toast } from "@studyhub/ui/toast";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { OnboardingStepLayout } from "@/components/onboarding/onboarding-step-layout";
import {
	RHYTHM_DEFAULT_DAYS,
	RHYTHM_DEFAULT_MINUTES,
	RhythmForm,
	type RhythmValue,
} from "@/components/onboarding/rhythm-form";
import { useOnboardingStep } from "@/components/onboarding/use-onboarding-step";
import { useRequireOnline } from "@/hooks/use-require-online";
import { api } from "@/lib/trpc-client";
import { toastError } from "@/lib/user-facing-error";

export const Route = createFileRoute("/onboarding/rhythm")({
	component: RhythmPage,
});

function RhythmPage(): React.ReactElement {
	const step = useOnboardingStep("rhythm");
	// Черновик приезжает с сервера — своей копии в `localStorage` не держим:
	// продолжить с другого устройства иначе было бы нечем.
	const progress = api.onboarding.getProgress.useQuery();

	if (progress.isPending) {
		return (
			<OnboardingStepLayout
				stepNumber={step.stepNumber}
				onBack={step.goBack}
				onNext={() => {}}
				onSkip={step.skip}
				nextDisabled
			>
				<div className="space-y-5">
					<Skeleton className="h-7 w-2/3" />
					<Skeleton className="h-11 w-full" />
					<Skeleton className="h-11 w-full" />
				</div>
			</OnboardingStepLayout>
		);
	}

	// Сбой запроса — не повод запирать человека на экране: показываем форму с
	// дефолтами. Потерять здесь нечего (черновика либо нет, либо он не доехал),
	// а тур должен проходиться и при неотвечающем `getProgress`.
	const draft = progress.data?.draft.rhythm;

	return (
		<RhythmStep
			step={step}
			initialValue={{
				minutes: draft?.dailyMinutesCap ?? RHYTHM_DEFAULT_MINUTES,
				days: draft ? [...draft.scheduleDays] : [...RHYTHM_DEFAULT_DAYS],
			}}
			initialCustomDraft={draft?.customDraft ?? ""}
		/>
	);
}

interface RhythmStepProps {
	step: ReturnType<typeof useOnboardingStep>;
	initialValue: RhythmValue;
	initialCustomDraft: string;
}

function RhythmStep({
	step,
	initialValue,
	initialCustomDraft,
}: RhythmStepProps): React.ReactElement {
	const { t } = useTranslation("onboarding");
	// Обе процедуры серверные (пересчёт расписания живёт в Inngest) — офлайн их
	// выполнить нельзя. Честный гейт вместо сетевой ошибки; пропуск при этом
	// остаётся доступен, так что офлайн не запирает человека на экране.
	const requireOnline = useRequireOnline();

	const [value, setValue] = useState<RhythmValue>(initialValue);
	const [customDraft, setCustomDraft] = useState(initialCustomDraft);
	const [isSaving, setIsSaving] = useState(false);

	const loadSettingsMut = api.studyPlan.loadSettings.update.useMutation();
	const streakSettingsMut = api.streaks.updateSettings.useMutation();
	const { mutateAsync: updateLoadSettings } = loadSettingsMut;
	const { mutateAsync: updateStreakSettings } = streakSettingsMut;

	/** Одна точка записи черновика: и переключатели, и поле ввода идут через неё. */
	function rememberDraft(next: RhythmValue, nextCustomDraft: string): void {
		const draft: OnboardingDraft = {
			rhythm: {
				dailyMinutesCap: next.minutes,
				scheduleDays: next.days,
				customDraft: nextCustomDraft,
			},
		};
		step.autosave.save(draft);
	}

	function handleChange(next: RhythmValue): void {
		setValue(next);
		rememberDraft(next, customDraft);
	}

	function handleCustomDraftChange(next: string): void {
		setCustomDraft(next);
		rememberDraft(value, next);
	}

	async function handleNext(): Promise<void> {
		if (isSaving) return;
		if (!requireOnline.guard()) return;
		setIsSaving(true);
		try {
			await updateLoadSettings({
				dailyMinutesCap: value.minutes,
				scheduleDays: value.days,
			});
		} catch (err) {
			setIsSaving(false);
			toastError(err, t("rhythm.saveFailed"), t);
			return;
		}
		try {
			await updateStreakSettings({ dailyGoalMinutes: value.minutes });
		} catch {
			// Календарь уже пересобирается — молчать нельзя, но и держать человека
			// на экране не за что: дневная норма правится в настройках.
			toast.warning(t("rhythm.goalFailed"));
		}
		setIsSaving(false);
		step.goNext();
	}

	return (
		<OnboardingStepLayout
			stepNumber={step.stepNumber}
			onBack={step.goBack}
			onNext={() => void handleNext()}
			onSkip={step.skip}
			isNextLoading={isSaving}
		>
			<div className="space-y-2">
				<h2 className="text-xl font-semibold tracking-tight text-foreground">
					{t("rhythm.title")}
				</h2>
				{/* Ценность — НА экране, а не в подсказке под полем (решение §13.4):
				    человек должен понимать, что именно он сейчас включает. */}
				<p className="text-base text-muted-foreground">{t("rhythm.lead")}</p>
			</div>

			<RhythmForm
				value={value}
				onChange={handleChange}
				customDraft={customDraft}
				onCustomDraftChange={handleCustomDraftChange}
				disabled={isSaving}
			/>
		</OnboardingStepLayout>
	);
}
